import { useMemo, useState } from "react"

import {
  Questionnaire,
  QuestionnaireSection,
} from "@/types/questionnaire/models"
import { getQuestionAnswerState } from "./questionAnswerState"

const isNeedsAnswerState = (
  state: ReturnType<typeof getQuestionAnswerState>,
) => {
  return state === "needs-answer" || state === "unanswered"
}

export type ResponseFilter = "all" | "needs-answer" | "low-confidence"

export const RESPONSE_FILTERS: ResponseFilter[] = [
  "all",
  "needs-answer",
  "low-confidence",
]

export const RESPONSE_FILTER_LABELS: Record<
  ResponseFilter,
  | "QUESTIONNAIRE_ANSWERS/FILTER_ALL"
  | "QUESTIONNAIRE_ANSWERS/FILTER_NEEDS_ANSWER"
  | "QUESTIONNAIRE_ANSWERS/FILTER_LOW_CONFIDENCE"
> = {
  all: "QUESTIONNAIRE_ANSWERS/FILTER_ALL",
  "needs-answer": "QUESTIONNAIRE_ANSWERS/FILTER_NEEDS_ANSWER",
  "low-confidence": "QUESTIONNAIRE_ANSWERS/FILTER_LOW_CONFIDENCE",
}

export interface FilteredQuestionnaireSection extends QuestionnaireSection {
  visibleResponsePairs: QuestionnaireSection["responsePairs"]
}

const matchesResponseFilter = (
  responsePair: QuestionnaireSection["responsePairs"][number],
  filter: ResponseFilter,
) => {
  if (filter === "all") {
    return true
  }

  const answerState = getQuestionAnswerState(responsePair)

  if (filter === "needs-answer") {
    return isNeedsAnswerState(answerState)
  }

  return answerState === filter
}

export const useQuestionListFilters = (
  questionnaire: Questionnaire | undefined,
) => {
  const [activeFilter, setActiveFilter] = useState<ResponseFilter>("all")

  const filterCounts = useMemo(() => {
    const counts: Record<ResponseFilter, number> = {
      all: 0,
      "needs-answer": 0,
      "low-confidence": 0,
    }

    if (!questionnaire) {
      return counts
    }

    for (const section of questionnaire.sections) {
      for (const pair of section.responsePairs) {
        counts.all += 1

        if (matchesResponseFilter(pair, "needs-answer")) {
          counts["needs-answer"] += 1
        }

        if (matchesResponseFilter(pair, "low-confidence")) {
          counts["low-confidence"] += 1
        }
      }
    }

    return counts
  }, [questionnaire])

  const visibleSections = useMemo<FilteredQuestionnaireSection[]>(() => {
    if (!questionnaire) {
      return []
    }

    return questionnaire.sections
      .map((section) => ({
        ...section,
        visibleResponsePairs: section.responsePairs.filter((pair) =>
          matchesResponseFilter(pair, activeFilter),
        ),
      }))
      .filter((section) => section.visibleResponsePairs.length > 0)
  }, [activeFilter, questionnaire])

  return {
    activeFilter,
    filterCounts,
    setActiveFilter,
    visibleSections,
  }
}
